import React from "react";
import { Building2, User, Calendar, Users, FileText, Shield } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";
import { useContentBlocks } from "../../hooks/useContentBlocks";
import { pickBlock } from "../../helpers/contentHelpers";
import { resolveIcon } from "../../helpers/iconResolver";

// Default icon mapping for company details
const DEFAULT_ICONS = {
  name: Building2,
  industry: FileText,
  license: FileText,
  representative: User,
  established: Calendar,
  employees: Users,
};

const CompanyInfo = ({ sectionData }) => {
  const { t, language } = useLanguage();
  const { info } = useSiteInfo();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? (key ? t(key) : ""));

  // Fetch content blocks for company details
  const sectionId = sectionData?.id;
  const { blocks: detailBlocks } = useContentBlocks(sectionId, "company-info", "feature");

  const sectionLabel = pick(sectionData?.labelEn, sectionData?.labelJa, "company.fields.license");
  const title = pick(sectionData?.titleEn, sectionData?.titleJa, "company.title");

  // Build company details from content blocks or fallback
  const companyDetails = detailBlocks.length > 0
    ? detailBlocks.map((block) => {
        const picked = pickBlock(block, language);
        const IconComp = resolveIcon(block.iconName) || Building2;
        return { icon: IconComp, label: picked.label, value: picked.value || picked.description };
      })
    : [
        { icon: Building2, label: t("company.fields.name"), value: info("company_name", t("company.companyName")) },
        { icon: FileText, label: t("company.fields.industry"), value: info("company_industry", t("company.industry")) },
        { icon: FileText, label: t("company.fields.license"), value: info("company_license", t("company.license")) },
        { icon: User, label: t("company.fields.representative"), value: info("company_representative", t("company.representative")) },
        { icon: Calendar, label: t("company.fields.established"), value: info("company_established", t("company.established")) },
        { icon: Users, label: t("company.fields.employees"), value: info("company_employees", t("company.employees")) },
      ];

  const description = pick(sectionData?.descriptionEn, sectionData?.descriptionJa, null)
    || info("company_description", t("company.description"));

  return (
    <section className="py-14 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-5 bg-accent-green rounded-full"></div>
              <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">{sectionLabel}</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-900">
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-2 bg-subidha-blue text-white px-4 py-2.5 rounded-xl text-xs font-bold border-2 border-subidha-blue/80 shrink-0">
            <Shield className="w-4 h-4 text-accent-green" />
            <span>{t("company.certBadge")}</span>
          </div>
        </div>

        {/* Company Details */}
        <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {companyDetails.map((detail, index) => (
              <div
                key={index}
                className={`flex items-center gap-4 px-6 py-4 border-b border-slate-100 ${
                  index % 2 === 0 ? "md:border-r md:border-slate-100" : ""
                } hover:bg-slate-50/60 transition-colors`}
              >
                <div className="flex-shrink-0 w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center">
                  <detail.icon className="w-4 h-4 text-subidha-blue" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
                    {detail.label}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 break-words">
                    {detail.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Description */}
          {description && (
            <div className="px-6 py-5 bg-slate-50/50 border-t border-slate-100">
              <p className="text-slate-600 leading-relaxed text-sm max-w-4xl">
                {description}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CompanyInfo;
